import {
    useQuery,
    useMutation,
    useQueryClient,
    useInfiniteQuery,
} from "@tanstack/react-query";
import {
    public_app_content_api_requests,
    app_content_api_requests,
    APP_CONTENT_API,
} from "../../app-content";
import { BaseDto } from "../../util/baseDto";
import { useNotification } from "../../../components/common/GlobalNotification";

const PATH = "/home-screen-images";
const BASE_QUERY_KEY = APP_CONTENT_API + PATH;


export interface HomeScreenImageItem extends BaseDto {
    id: number;
    imageUrl: string | null;
    title?: string | null;
    subtitle?: string | null;
    ctaLabel?: string | null;
    linkUrl?: string | null;
    /** 'INTERNAL', 'EXTERNAL', or 'KEYWORD'. NULL when no link set. */
    linkType?: string | null;
    effect?: string;
    effectConfig?: Record<string, any>;
    sortOrder?: number;
    isActive?: boolean;
    /** false = hero carousel (top). true = footer/KPI-card area (bottom). */
    isFooterBanner?: boolean;
}

export interface HomeScreenImageApiResponse {
    metaData: {
        size: number;
        totalPages: number;
        page: number;
        totalElements: number;
    };
    data: HomeScreenImageItem[];
}


// ── Shared metadata type (mirrors the non-file fields on HomeScreenImageDTO) ─
interface BannerMeta {
    title?: string;
    subtitle?: string;
    ctaLabel?: string;
    linkUrl?: string;
    linkType?: string;
    effect?: string;
    effectConfig?: Record<string, any> | null;
    sortOrder?: number;
    isActive?: boolean;
    isFooterBanner?: boolean;
}

// Appends all provided metadata fields to an existing FormData.
// effectConfig is JSON-stringified into a single "effectConfigJson" form field
// so the multipart endpoint can parse it back to a Map<String,Object>.
const appendMeta = (formData: FormData, meta: BannerMeta) => {
    if (meta.title          !== undefined) formData.append("title",          meta.title ?? "");
    if (meta.subtitle       !== undefined) formData.append("subtitle",       meta.subtitle ?? "");
    if (meta.ctaLabel       !== undefined) formData.append("ctaLabel",       meta.ctaLabel ?? "");
    if (meta.linkUrl        !== undefined) formData.append("linkUrl",        meta.linkUrl ?? "");
    if (meta.linkType       !== undefined) formData.append("linkType",       meta.linkType ?? "");
    if (meta.effect         !== undefined) formData.append("effect",         meta.effect ?? "none");
    if (meta.sortOrder      !== undefined) formData.append("sortOrder",      String(meta.sortOrder));
    if (meta.isActive       !== undefined) formData.append("isActive",       String(meta.isActive));
    if (meta.isFooterBanner !== undefined) formData.append("isFooterBanner", String(meta.isFooterBanner));
    if (meta.effectConfig !== undefined) {
        formData.append(
            "effectConfigJson",
            meta.effectConfig ? JSON.stringify(meta.effectConfig) : ""
        );
    }
};

// Marks all home-screen-image queries stale. The public carousel query will
// re-fetch automatically the next time it renders with stale data.
const invalidateAll = (queryClient: ReturnType<typeof useQueryClient>) => {
    queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
};

const useCreateHomeScreenImage = () => {
    const queryClient = useQueryClient();
    const { setSuccessNotification, setErrorNotification } = useNotification();

    return useMutation({
        mutationFn: async ({ file, ...meta }: { file: File } & BannerMeta) => {
            const formData = new FormData();
            formData.append("file", file);
            appendMeta(formData, meta);
            return app_content_api_requests.post(PATH, formData);
        },
        onSuccess: () => {
            invalidateAll(queryClient);
            setSuccessNotification("Banner uploaded successfully");
        },
        onError: () => {
            setErrorNotification("Failed to upload banner");
        },
    });
};

const useUpdateHomeScreenImage = () => {
    const queryClient = useQueryClient();
    const { setSuccessNotification, setErrorNotification } = useNotification();

    return useMutation({
        mutationFn: async ({ id, file, ...meta }: { id: number; file: File } & BannerMeta) => {
            const formData = new FormData();
            formData.append("file", file);
            appendMeta(formData, meta);
            return app_content_api_requests.put(`${PATH}/${id}`, formData);
        },
        onSuccess: () => {
            invalidateAll(queryClient);
            setSuccessNotification("Banner updated successfully");
        },
        onError: () => {
            setErrorNotification("Failed to update banner");
        },
    });
};

// PATCH /{id}/metadata — JSON body, no file re-upload required.
// Used when the admin edits title/effect/config/etc. without replacing the photo.
const useUpdateHomeScreenImageMetadata = () => {
    const queryClient = useQueryClient();
    const { setSuccessNotification, setErrorNotification } = useNotification();

    return useMutation({
        mutationFn: async ({ id, ...meta }: { id: number } & BannerMeta) =>
            app_content_api_requests.post(`${PATH}/${id}/edit`, meta),
        onSuccess: () => {
            invalidateAll(queryClient);
            setSuccessNotification("Banner updated successfully");
        },
        onError: () => {
            setErrorNotification("Failed to update banner");
        },
    });
};

const useDeleteHomeScreenImage = () => {
    const queryClient = useQueryClient();
    const { setSuccessNotification, setErrorNotification } = useNotification();

    return useMutation({
        mutationFn: async (id: number) =>
            app_content_api_requests.delete(`${PATH}/${id}`),
        onSuccess: () => {
            invalidateAll(queryClient);
            setSuccessNotification("Image deleted successfully");
        },
        onError: () => {
            setErrorNotification("Failed to delete image");
        },
    });
};

const useGetHomeScreenImages = (size = 10) => {
    return useInfiniteQuery<HomeScreenImageApiResponse, Error>({
        queryKey: [BASE_QUERY_KEY, "public", size],
        queryFn: async ({ pageParam = 0 }) =>
            public_app_content_api_requests.get(
                `${PATH}/public/all?page=${pageParam}&size=${size}&sort=sortOrder,asc`
            ),
        getNextPageParam: (lastPage) => {
            const { page, totalPages } = lastPage.metaData;
            return page + 1 < totalPages ? page + 1 : undefined;
        },
        // Normalise stale/cached responses: guarantee effect is never undefined
        // downstream — a pre-V25 cache entry won't have the field at all.
        select: (data) => ({
            ...data,
            pages: data.pages.map((page) => ({
                ...page,
                data: page.data.map((item) => ({
                    ...item,
                    effect: item.effect ?? 'none',
                })),
            })),
        }),
        // true = re-fetch when the component mounts and the cache is stale
        // (e.g. after an admin mutation invalidated the query while the carousel
        // was already rendered on the home page).
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });
};

const useGetAllBanners = () => {
    return useQuery<HomeScreenImageItem[], Error>({
        queryKey: [BASE_QUERY_KEY, "public", "banners"],
        queryFn: () =>
            public_app_content_api_requests.get(`${PATH}/public/banners`),
        // Normalise: guarantee effect is never undefined downstream.
        select: (data) =>
            data.map((item: HomeScreenImageItem) => ({ ...item, effect: item.effect ?? 'none' })),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });
};

const useGetHomeScreenImageById = (id: number) => {
    return useQuery({
        queryKey: [BASE_QUERY_KEY, "detail", id],
        queryFn: async (): Promise<HomeScreenImageItem> =>
            app_content_api_requests.get(`${PATH}/${id}`),
        enabled: !!id,
    });
};
export {
    BASE_QUERY_KEY as HOME_SCREEN_IMAGES_BASE_QUERY_KEY,
    useCreateHomeScreenImage,
    useUpdateHomeScreenImage,
    useUpdateHomeScreenImageMetadata,
    useDeleteHomeScreenImage,
    useGetHomeScreenImages,
    useGetAllBanners,
    useGetHomeScreenImageById
};


